<template>
    <PanelPage>
        <template v-slot:title>
            <SmartBreadcrumbs
                type="InertiaLink"
                :pages="[
                    {
                        name: $t('messages.attendance_review'),
                        href: meta.links.attendance_index,
                    },
                    {
                        name: group.name,
                        href: meta.links.attendance_group_show,
                    },
                    {
                        name: attendance.title,
                        href: null,
                    },
                ]"
            />
        </template>

        <div class="bg-gray-100 py-4 px-2 rounded" v-if="attendance.comment">
            <div class="font-bold">
                {{ $t('messages.comment') }}
            </div>
            <p>{{ attendance.comment }}</p>
        </div>

        <div class="md:grid md:grid-cols-2 md:gap-6">
            <div>
                <div class="mt-3 flex">
                    <AppButton element="InertiaLink" v-if="meta.can.edit" :href="meta.links.edit">
                        {{ $t('messages.edit') }}
                    </AppButton>

                    <DropdownButton>
                        <template v-slot:content>
                            <InertiaLink
                                :href="meta.links.group_show"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.group') }}
                            </InertiaLink>
                            <InertiaLink
                                :href="meta.links.attendance_select"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.rollcall') }}
                            </InertiaLink>
                            <InertiaLink
                                :href="meta.links.attendance_group_show"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.attendance_review') }}
                            </InertiaLink>
                            <InertiaLink
                                :href="meta.links.attendance_group_monthly_report"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.monthly_attendance_report') }}
                            </InertiaLink>
                            <InertiaLink
                                :href="meta.links.foul_group_create"
                                class="block px-4 py-2 text-sm leading-5 text-gray-700 hover:bg-gray-100 hover:text-gray-900 focus:outline-none focus:bg-gray-100 focus:text-gray-900"
                                role="menuitem"
                            >
                                {{ $t('messages.issue_foul') }}
                            </InertiaLink>
                        </template>
                    </DropdownButton>
                </div>
            </div>
            <div>
                <div>
                    <apexchart type="pie" height="150" :series="chart.series" :options="chart"> </apexchart>
                </div>
                <div class="flex gap-x-2">
                    <div class="flex items-center space-x-3 lg:pl-2">
                        <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-red-400"></div>
                        <span>{{ $t('messages.present') }} ({{ attendance.present_count }})</span>
                    </div>
                    <div class="flex items-center space-x-3 lg:pl-2">
                        <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-blue-400"></div>
                        <span>{{ $t('messages.absent') }} ({{ attendance.absent_count }})</span>
                    </div>
                </div>
            </div>
        </div>

        <div class="flex flex-col mt-5">
            <div class="-my-2 py-2 overflow-x-auto sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
                <div
                    class="align-middle inline-block min-w-full shadow overflow-hidden sm:rounded-lg border-b border-gray-200"
                >
                    <table class="min-w-full divide-y divide-gray-200">
                        <thead>
                            <tr>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.student') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.absent') }}
                                </th>
                                <th
                                    class="px-6 py-3 bg-gray-50 text-left text-xs leading-4 font-medium text-gray-500 uppercase tracking-wider"
                                >
                                    {{ $t('messages.delay') }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="(student, index) in attendance.students"
                                :key="student.id"
                                :class="index % 2 === 0 ? 'bg-white' : 'bg-gray-50'"
                            >
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 font-medium text-gray-900">
                                    <InertiaLink :href="student.links.student_show" class="text-indigo-500">
                                        {{ student.full_name }}
                                    </InertiaLink>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div class="flex items-center space-x-3 lg:pl-2">
                                        <div v-if="student.absent">
                                            <div v-if="student.is_justified">
                                                <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-green-400"></div>
                                            </div>
                                            <div v-else>
                                                <div class="shrink-0 w-2.5 h-2.5 rounded-full bg-red-400"></div>
                                            </div>
                                        </div>
                                        <span>{{ student.justification }}</span>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm leading-5 text-gray-500">
                                    <div v-if="student.delay">
                                        <span>{{ student.delay_name }}</span>
                                    </div>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    </PanelPage>
</template>

<script>
import Layout from '@/Shared/Layout.vue';
import PanelPage from '@/Shared/PanelPage.vue';
import DropdownButton from '@/components/DropdownButton.vue';
import AppButton from '@/components/AppButton.vue';

export default {
    components: {
        AppButton,
        DropdownButton,
        PanelPage,
    },
    layout: Layout,
    name: 'Show',
    props: ['meta', 'attendance', 'group', 'chart'],
};
</script>
